import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import type { Group, Mesh } from 'three'
import type { BuildingType, Owner, ResourceKind, UnitType } from '../game'

const PALETTE = {
  basalt: '#151c21',
  basaltLight: '#283238',
  bone: '#d9d0b7',
  boneDark: '#8c8778',
  brass: '#a77936',
  brassDark: '#5d4527',
  amber: '#ffb52e',
  amberDark: '#7a4814',
  genome: '#78f0b5',
  fossil: '#87949a',
  alloy: '#c6d6d8',
  player: '#2de7ff',
  ai: '#ff596a',
  dormant: '#465158',
  dormantBone: '#666962',
} as const

const TAU = Math.PI * 2

function teamColor(owner: Owner) {
  return owner === 'player' ? PALETTE.player : PALETTE.ai
}

function activeAccent(owner: Owner, powered: boolean) {
  return powered ? teamColor(owner) : PALETTE.dormant
}

function clamp01(value: number) {
  return Math.max(0, Math.min(1, value))
}

function BoneMaterial({ powered = true }: { powered?: boolean }) {
  return (
    <meshStandardMaterial
      color={powered ? PALETTE.bone : PALETTE.dormantBone}
      roughness={0.72}
      metalness={0.08}
    />
  )
}

function BasaltMaterial({ powered = true }: { powered?: boolean }) {
  return (
    <meshStandardMaterial
      color={powered ? PALETTE.basalt : '#20272a'}
      roughness={0.8}
      metalness={0.16}
    />
  )
}

function BrassMaterial({ powered = true }: { powered?: boolean }) {
  return (
    <meshStandardMaterial
      color={powered ? PALETTE.brass : PALETTE.brassDark}
      roughness={0.42}
      metalness={0.68}
    />
  )
}

function TeamMaterial({ owner, powered = true, intensity = 1.25 }: {
  owner: Owner
  powered?: boolean
  intensity?: number
}) {
  const color = activeAccent(owner, powered)
  return (
    <meshStandardMaterial
      color={color}
      emissive={color}
      emissiveIntensity={powered ? intensity : 0.04}
      roughness={0.3}
      metalness={0.35}
    />
  )
}

function AmberGlass({ powered = true, opacity = 0.62 }: { powered?: boolean; opacity?: number }) {
  return (
    <meshStandardMaterial
      color={powered ? PALETTE.amber : PALETTE.amberDark}
      emissive={powered ? PALETTE.amberDark : '#17130e'}
      emissiveIntensity={powered ? 0.65 : 0.02}
      transparent
      opacity={opacity}
      roughness={0.18}
      metalness={0.08}
      depthWrite={false}
    />
  )
}

function BuildingPedestal({ owner, powered }: { owner: Owner; powered: boolean }) {
  return (
    <group>
      <mesh position={[0, 0.07, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.5, 0.54, 0.14, 8]} />
        <BasaltMaterial powered={powered} />
      </mesh>
      <mesh position={[0, 0.14, 0]} castShadow>
        <cylinderGeometry args={[0.41, 0.45, 0.055, 8]} />
        <BoneMaterial powered={powered} />
      </mesh>
      <mesh position={[0, 0.174, 0]}>
        <torusGeometry args={[0.35, 0.022, 5, 24]} />
        <TeamMaterial owner={owner} powered={powered} intensity={0.8} />
      </mesh>
    </group>
  )
}

function BuildingStatus({
  owner,
  selected,
  powered,
  progress,
  stockCount,
}: {
  owner: Owner
  selected: boolean
  powered: boolean
  progress: number
  stockCount: number
}) {
  const accent = activeAccent(owner, powered)
  const pipCount = Math.max(0, Math.min(5, Math.floor(stockCount)))
  return (
    <group>
      {selected && (
        <mesh position={[0, 0.025, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.58, 0.026, 6, 48]} />
          <meshBasicMaterial color={teamColor(owner)} toneMapped={false} />
        </mesh>
      )}
      {progress > 0 && progress < 1 && (
        <mesh position={[0, 0.04, 0]} rotation={[Math.PI / 2, 0, -Math.PI / 2]}>
          <torusGeometry args={[0.525, 0.018, 5, 40, Math.max(0.04, progress) * TAU]} />
          <meshBasicMaterial color={accent} toneMapped={false} />
        </mesh>
      )}
      {Array.from({ length: pipCount }, (_, index) => {
        const x = (index - (pipCount - 1) / 2) * 0.11
        return (
          <mesh key={index} position={[x, 0.18, 0.49]} rotation={[0, Math.PI / 4, 0]} castShadow>
            <octahedronGeometry args={[0.045, 0]} />
            <meshStandardMaterial
              color={accent}
              emissive={accent}
              emissiveIntensity={powered ? 0.85 : 0}
              roughness={0.34}
            />
          </mesh>
        )
      })}
    </group>
  )
}

interface BuildingVisualProps {
  owner: Owner
  powered: boolean
  progress: number
}

function HeartforgeModel({ owner, powered }: BuildingVisualProps) {
  const heart = useRef<Group>(null)
  const crown = useRef<Group>(null)

  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    if (heart.current) {
      const pulse = powered ? 1 + Math.pow(Math.max(0, Math.sin(time * 3.1)), 8) * 0.13 : 0.96
      heart.current.scale.setScalar(pulse)
      heart.current.rotation.y = time * (powered ? 0.38 : 0.04)
    }
    if (crown.current) crown.current.rotation.y = -time * (powered ? 0.24 : 0.025)
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      <mesh position={[0, 0.37, 0]} castShadow>
        <cylinderGeometry args={[0.2, 0.28, 0.42, 8]} />
        <BrassMaterial powered={powered} />
      </mesh>
      <group ref={heart} position={[0, 0.68, 0]}>
        <mesh castShadow>
          <dodecahedronGeometry args={[0.22, 0]} />
          <TeamMaterial owner={owner} powered={powered} intensity={2.4} />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.29, 0.035, 6, 24]} />
          <BoneMaterial powered={powered} />
        </mesh>
        <mesh rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.29, 0.027, 6, 24]} />
          <BrassMaterial powered={powered} />
        </mesh>
      </group>
      <group ref={crown} position={[0, 0.68, 0]}>
        {[0, 1, 2, 3].map((index) => {
          const angle = index * Math.PI / 2
          return (
            <mesh
              key={index}
              position={[Math.cos(angle) * 0.36, 0, Math.sin(angle) * 0.36]}
              rotation={[0, -angle, -0.25]}
              castShadow
            >
              <coneGeometry args={[0.055, 0.34, 5]} />
              <BoneMaterial powered={powered} />
            </mesh>
          )
        })}
      </group>
      <mesh position={[0, 1.02, 0]} castShadow>
        <coneGeometry args={[0.085, 0.28, 6]} />
        <BrassMaterial powered={powered} />
      </mesh>
    </group>
  )
}

function VeinwayModel({ owner, powered }: BuildingVisualProps) {
  return (
    <group>
      <mesh position={[0, 0.055, 0]} receiveShadow>
        <boxGeometry args={[1, 0.11, 0.4]} />
        <BasaltMaterial powered={powered} />
      </mesh>
      {[-0.17, 0.17].map((z) => (
        <mesh key={z} position={[0, 0.13, z]} castShadow>
          <boxGeometry args={[0.94, 0.1, 0.075]} />
          <BoneMaterial powered={powered} />
        </mesh>
      ))}
      <mesh position={[0, 0.14, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.035, 0.035, 0.86, 8]} />
        <TeamMaterial owner={owner} powered={powered} intensity={1.5} />
      </mesh>
      {[-0.34, 0, 0.34].map((x) => (
        <mesh key={x} position={[x, 0.145, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[0.1, 0.022, 5, 12]} />
          <BrassMaterial powered={powered} />
        </mesh>
      ))}
    </group>
  )
}

function FossilRigModel({ owner, powered, progress }: BuildingVisualProps) {
  const drill = useRef<Group>(null)
  useFrame(({ clock }) => {
    if (!drill.current) return
    drill.current.rotation.y = powered ? clock.elapsedTime * 5.5 : 0
    drill.current.position.y = 0.39 + (powered ? Math.sin(clock.elapsedTime * 7) * 0.018 : 0) - progress * 0.025
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      {[-1, 1].map((side) => (
        <mesh key={side} position={[side * 0.27, 0.46, 0]} rotation={[0, 0, side * -0.22]} castShadow>
          <boxGeometry args={[0.1, 0.6, 0.12]} />
          <BoneMaterial powered={powered} />
        </mesh>
      ))}
      <mesh position={[0, 0.73, 0]} castShadow>
        <boxGeometry args={[0.68, 0.13, 0.18]} />
        <BasaltMaterial powered={powered} />
      </mesh>
      <mesh position={[0, 0.75, 0.1]}>
        <boxGeometry args={[0.4, 0.045, 0.035]} />
        <TeamMaterial owner={owner} powered={powered} />
      </mesh>
      <group ref={drill}>
        <mesh position={[0, 0.08, 0]} castShadow>
          <cylinderGeometry args={[0.075, 0.075, 0.52, 8]} />
          <BrassMaterial powered={powered} />
        </mesh>
        <mesh position={[0, -0.23, 0]} castShadow>
          <coneGeometry args={[0.16, 0.3, 7]} />
          <meshStandardMaterial color={powered ? PALETTE.fossil : PALETTE.dormant} roughness={0.52} metalness={0.5} />
        </mesh>
      </group>
    </group>
  )
}

function AmberTapModel({ owner, powered, progress }: BuildingVisualProps) {
  const pump = useRef<Group>(null)
  const liquid = useRef<Mesh>(null)
  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    if (pump.current) pump.current.rotation.z = -0.22 + (powered ? Math.sin(time * 2.8) * 0.16 : 0)
    if (liquid.current) {
      const fill = 0.86 + progress * 0.14 + (powered ? Math.sin(time * 2.2) * 0.025 : 0)
      liquid.current.scale.y = fill
    }
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      <mesh position={[-0.12, 0.47, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.29, 0.57, 10]} />
        <AmberGlass powered={powered} />
      </mesh>
      <mesh ref={liquid} position={[-0.12, 0.43, 0]} castShadow>
        <cylinderGeometry args={[0.19, 0.21, 0.37, 10]} />
        <meshStandardMaterial
          color={powered ? PALETTE.amber : PALETTE.amberDark}
          emissive={powered ? PALETTE.amberDark : '#17130e'}
          emissiveIntensity={powered ? 1 : 0.02}
          roughness={0.28}
        />
      </mesh>
      {[0.21, 0.7].map((y) => (
        <mesh key={y} position={[-0.12, y, 0]}>
          <torusGeometry args={[0.265, 0.035, 6, 18]} />
          <BrassMaterial powered={powered} />
        </mesh>
      ))}
      <mesh position={[0.31, 0.36, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.16, 0.4, 8]} />
        <BasaltMaterial powered={powered} />
      </mesh>
      <group ref={pump} position={[0.29, 0.62, 0]}>
        <mesh position={[0.16, 0, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <boxGeometry args={[0.08, 0.39, 0.09]} />
          <BoneMaterial powered={powered} />
        </mesh>
        <mesh position={[-0.02, 0, 0]}>
          <sphereGeometry args={[0.09, 8, 6]} />
          <TeamMaterial owner={owner} powered={powered} />
        </mesh>
      </group>
    </group>
  )
}

function AlloyForgeModel({ owner, powered, progress }: BuildingVisualProps) {
  const fan = useRef<Mesh>(null)
  const press = useRef<Group>(null)
  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    if (fan.current) fan.current.rotation.z = powered ? -time * 4.5 : 0
    if (press.current) {
      const stroke = powered ? Math.pow(Math.max(0, Math.sin(time * 3.4)), 6) : 0
      press.current.position.y = 0.64 - stroke * 0.15 - progress * 0.025
    }
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      {[-0.27, 0.27].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh position={[0, 0.39, 0]} castShadow>
            <boxGeometry args={[0.32, 0.45, 0.42]} />
            <BasaltMaterial powered={powered} />
          </mesh>
          <mesh position={[0, 0.39, 0.222]}>
            <boxGeometry args={[0.18, 0.17, 0.025]} />
            <meshStandardMaterial
              color={powered ? '#ff713d' : PALETTE.dormant}
              emissive={powered ? '#ff4a20' : '#111314'}
              emissiveIntensity={powered ? 1.7 : 0.02}
              roughness={0.32}
            />
          </mesh>
          <mesh position={[0, 0.75, 0]} castShadow>
            <cylinderGeometry args={[0.075, 0.11, 0.28, 7]} />
            <BrassMaterial powered={powered} />
          </mesh>
        </group>
      ))}
      <group ref={press} position={[0, 0.64, 0]}>
        <mesh castShadow>
          <boxGeometry args={[0.2, 0.25, 0.26]} />
          <BoneMaterial powered={powered} />
        </mesh>
        <mesh position={[0, 0.15, 0]}>
          <boxGeometry args={[0.1, 0.08, 0.16]} />
          <TeamMaterial owner={owner} powered={powered} />
        </mesh>
      </group>
      <mesh ref={fan} position={[0, 0.42, -0.235]} rotation={[0, 0, 0]}>
        <torusGeometry args={[0.12, 0.028, 6, 16]} />
        <BrassMaterial powered={powered} />
      </mesh>
    </group>
  )
}

function GeneVatModel({ owner, powered, progress }: BuildingVisualProps) {
  const helix = useRef<Group>(null)
  useFrame(({ clock }) => {
    if (!helix.current) return
    helix.current.rotation.y = powered ? clock.elapsedTime * (1.2 + progress * 1.3) : 0
    helix.current.position.y = 0.48 + (powered ? Math.sin(clock.elapsedTime * 2) * 0.025 : 0)
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      <mesh position={[0, 0.53, 0]} castShadow>
        <cylinderGeometry args={[0.31, 0.34, 0.68, 12]} />
        <meshStandardMaterial
          color={powered ? PALETTE.genome : PALETTE.dormant}
          emissive={powered ? '#174c3b' : '#111615'}
          emissiveIntensity={powered ? 0.55 : 0.01}
          transparent
          opacity={0.38}
          roughness={0.14}
          depthWrite={false}
        />
      </mesh>
      {[0.2, 0.86].map((y) => (
        <mesh key={y} position={[0, y, 0]}>
          <torusGeometry args={[0.31, 0.038, 6, 20]} />
          <BrassMaterial powered={powered} />
        </mesh>
      ))}
      <group ref={helix} position={[0, 0.48, 0]}>
        {[-0.2, 0, 0.2].map((y, index) => (
          <group key={y} position={[0, y, 0]} rotation={[0, index * 1.9, 0]}>
            <mesh position={[0.13, 0, 0]} castShadow>
              <sphereGeometry args={[0.07, 7, 5]} />
              <TeamMaterial owner={owner} powered={powered} intensity={1.6} />
            </mesh>
            <mesh position={[-0.13, 0, 0]} castShadow>
              <sphereGeometry args={[0.07, 7, 5]} />
              <meshStandardMaterial
                color={powered ? PALETTE.genome : PALETTE.dormant}
                emissive={powered ? '#245d48' : '#111615'}
                emissiveIntensity={powered ? 1.2 : 0}
              />
            </mesh>
            <mesh rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.018, 0.018, 0.26, 6]} />
              <BoneMaterial powered={powered} />
            </mesh>
          </group>
        ))}
      </group>
      <mesh position={[0, 0.96, 0]} castShadow>
        <cylinderGeometry args={[0.17, 0.24, 0.17, 8]} />
        <BoneMaterial powered={powered} />
      </mesh>
    </group>
  )
}

function DynamoModel({ owner, powered, progress }: BuildingVisualProps) {
  const rotor = useRef<Group>(null)
  useFrame(({ clock }) => {
    if (!rotor.current) return
    const time = clock.elapsedTime
    rotor.current.rotation.y = powered ? time * (2.2 + progress) : 0
    rotor.current.rotation.z = powered ? Math.sin(time * 0.8) * 0.18 : 0
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      <mesh position={[0, 0.48, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.2, 0.62, 8]} />
        <BrassMaterial powered={powered} />
      </mesh>
      <group ref={rotor} position={[0, 0.58, 0]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.34, 0.05, 6, 24]} />
          <BoneMaterial powered={powered} />
        </mesh>
        <mesh rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.29, 0.035, 6, 24]} />
          <TeamMaterial owner={owner} powered={powered} intensity={1.8} />
        </mesh>
        <mesh castShadow>
          <icosahedronGeometry args={[0.14, 0]} />
          <meshStandardMaterial
            color={activeAccent(owner, powered)}
            emissive={activeAccent(owner, powered)}
            emissiveIntensity={powered ? 2 : 0.02}
            roughness={0.25}
          />
        </mesh>
      </group>
      {[-0.36, 0.36].map((x) => (
        <mesh key={x} position={[x, 0.36, 0]} castShadow>
          <coneGeometry args={[0.07, 0.45, 6]} />
          <BoneMaterial powered={powered} />
        </mesh>
      ))}
    </group>
  )
}

function IncubatorModel({ owner, powered, progress }: BuildingVisualProps) {
  const pod = useRef<Group>(null)
  const embryo = useRef<Group>(null)
  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    if (pod.current) {
      pod.current.position.y = 0.58 + (powered ? Math.sin(time * 1.8) * 0.018 : 0)
      pod.current.rotation.y = powered ? Math.sin(time * 0.7) * 0.06 : 0
    }
    if (embryo.current) {
      const pulse = powered ? 0.9 + progress * 0.16 + Math.sin(time * 3.1) * 0.025 : 0.86
      embryo.current.scale.setScalar(pulse)
      embryo.current.rotation.y = -time * (powered ? 0.45 : 0.02)
    }
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      <group ref={pod} position={[0, 0.58, 0]}>
        <mesh scale={[0.75, 1.15, 0.75]} castShadow>
          <sphereGeometry args={[0.37, 12, 9]} />
          <AmberGlass powered={powered} opacity={0.42} />
        </mesh>
        <group ref={embryo}>
          <mesh position={[0.02, -0.02, 0]} scale={[1.2, 0.72, 0.65]} castShadow>
            <dodecahedronGeometry args={[0.14, 0]} />
            <TeamMaterial owner={owner} powered={powered} intensity={1.8} />
          </mesh>
          <mesh position={[0.18, 0.04, 0]} scale={[1, 0.7, 0.7]}>
            <dodecahedronGeometry args={[0.08, 0]} />
            <BoneMaterial powered={powered} />
          </mesh>
          <mesh position={[-0.16, 0.01, 0]} rotation={[0, 0, Math.PI / 2]}>
            <coneGeometry args={[0.055, 0.24, 5]} />
            <BrassMaterial powered={powered} />
          </mesh>
        </group>
        {[-1, 1].map((side) => (
          <mesh key={side} position={[side * 0.29, 0, 0]} rotation={[0, 0, side * 0.22]} castShadow>
            <boxGeometry args={[0.07, 0.72, 0.1]} />
            <BoneMaterial powered={powered} />
          </mesh>
        ))}
        <mesh position={[0, 0.4, 0]}>
          <torusGeometry args={[0.24, 0.035, 6, 18]} />
          <BrassMaterial powered={powered} />
        </mesh>
      </group>
    </group>
  )
}

function SpineTurretModel({ owner, powered, progress }: BuildingVisualProps) {
  const turret = useRef<Group>(null)
  const barrels = useRef<Group>(null)
  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    if (turret.current) turret.current.rotation.y = powered ? Math.sin(time * 0.72) * 0.78 : 0
    if (barrels.current) {
      const recoil = powered ? Math.pow(Math.max(0, Math.sin(time * 4.4)), 18) * 0.08 : 0
      barrels.current.position.z = -recoil - progress * 0.01
    }
  })

  return (
    <group>
      <BuildingPedestal owner={owner} powered={powered} />
      <mesh position={[0, 0.38, 0]} castShadow>
        <cylinderGeometry args={[0.24, 0.32, 0.38, 8]} />
        <BrassMaterial powered={powered} />
      </mesh>
      <group ref={turret} position={[0, 0.65, 0]}>
        <mesh castShadow>
          <dodecahedronGeometry args={[0.26, 0]} />
          <BasaltMaterial powered={powered} />
        </mesh>
        <mesh position={[0, 0.08, 0]} scale={[1.12, 0.42, 0.9]}>
          <dodecahedronGeometry args={[0.24, 0]} />
          <BoneMaterial powered={powered} />
        </mesh>
        <mesh position={[0, 0.19, 0]}>
          <boxGeometry args={[0.3, 0.055, 0.22]} />
          <TeamMaterial owner={owner} powered={powered} intensity={1.5} />
        </mesh>
        <group ref={barrels}>
          {[-0.1, 0.1].map((x) => (
            <group key={x} position={[x, 0.02, 0.31]} rotation={[Math.PI / 2, 0, 0]}>
              <mesh castShadow>
                <coneGeometry args={[0.055, 0.58, 6]} />
                <meshStandardMaterial color={powered ? PALETTE.alloy : PALETTE.dormant} roughness={0.34} metalness={0.7} />
              </mesh>
              <mesh position={[0, -0.22, 0]}>
                <cylinderGeometry args={[0.07, 0.07, 0.08, 7]} />
                <TeamMaterial owner={owner} powered={powered} />
              </mesh>
            </group>
          ))}
        </group>
        {[-0.24, 0.24].map((x) => (
          <mesh key={x} position={[x, 0.17, -0.04]} rotation={[0, 0, x > 0 ? -0.35 : 0.35]} castShadow>
            <coneGeometry args={[0.055, 0.26, 5]} />
            <BoneMaterial powered={powered} />
          </mesh>
        ))}
      </group>
    </group>
  )
}

function buildingVisual(type: BuildingType, props: BuildingVisualProps) {
  switch (type) {
    case 'heartforge': return <HeartforgeModel {...props} />
    case 'veinway': return <VeinwayModel {...props} />
    case 'fossilRig': return <FossilRigModel {...props} />
    case 'amberTap': return <AmberTapModel {...props} />
    case 'alloyForge': return <AlloyForgeModel {...props} />
    case 'geneVat': return <GeneVatModel {...props} />
    case 'dynamo': return <DynamoModel {...props} />
    case 'incubator': return <IncubatorModel {...props} />
    case 'spineTurret': return <SpineTurretModel {...props} />
  }
}

export function BuildingModel({
  type,
  owner,
  selected = false,
  powered = true,
  progress = 0,
  stockCount = 0,
}: {
  type: BuildingType
  owner: Owner
  selected?: boolean
  powered?: boolean
  progress?: number
  stockCount?: number
}) {
  const safeProgress = clamp01(progress)
  return (
    <group>
      <BuildingStatus
        owner={owner}
        selected={selected}
        powered={powered}
        progress={safeProgress}
        stockCount={stockCount}
      />
      {buildingVisual(type, { owner, powered, progress: safeProgress })}
    </group>
  )
}

interface UnitVisualProps {
  owner: Owner
  attacking: boolean
}

function ClawpackModel({ owner, attacking }: UnitVisualProps) {
  const motion = useRef<Group>(null)
  const nearLeg = useRef<Group>(null)
  const farLeg = useRef<Group>(null)
  const head = useRef<Group>(null)
  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    const gait = Math.sin(time * 10)
    if (motion.current) motion.current.position.y = 0.015 + Math.abs(gait) * 0.018
    if (nearLeg.current) nearLeg.current.rotation.z = gait * 0.42
    if (farLeg.current) farLeg.current.rotation.z = -gait * 0.42
    if (head.current) head.current.position.x = 0.32 + (attacking ? Math.sin(time * 18) * 0.07 : 0)
  })

  return (
    <group ref={motion}>
      <mesh position={[-0.03, 0.36, 0]} scale={[1.45, 0.85, 0.78]} castShadow>
        <dodecahedronGeometry args={[0.25, 0]} />
        <BasaltMaterial />
      </mesh>
      <mesh position={[0, 0.47, 0]} scale={[1.1, 0.32, 0.9]}>
        <dodecahedronGeometry args={[0.22, 0]} />
        <TeamMaterial owner={owner} intensity={1.15} />
      </mesh>
      <group ref={head} position={[0.32, 0.47, 0]}>
        <mesh scale={[1.2, 0.72, 0.82]} castShadow>
          <dodecahedronGeometry args={[0.18, 0]} />
          <BoneMaterial />
        </mesh>
        <mesh position={[0.14, -0.05, 0]} rotation={[0, 0, -0.18]} castShadow>
          <boxGeometry args={[0.25, 0.1, 0.18]} />
          <BasaltMaterial />
        </mesh>
        {[-0.075, 0.075].map((z) => (
          <mesh key={z} position={[0.12, 0.055, z]}>
            <sphereGeometry args={[0.024, 6, 4]} />
            <TeamMaterial owner={owner} intensity={2.2} />
          </mesh>
        ))}
      </group>
      <mesh position={[-0.39, 0.38, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <coneGeometry args={[0.09, 0.52, 6]} />
        <BrassMaterial />
      </mesh>
      {[{ ref: nearLeg, z: 0.16 }, { ref: farLeg, z: -0.16 }].map(({ ref, z }) => (
        <group key={z} ref={ref} position={[-0.02, 0.29, z]}>
          <mesh position={[0, -0.11, 0]} rotation={[0, 0, -0.2]} castShadow>
            <boxGeometry args={[0.085, 0.27, 0.075]} />
            <BoneMaterial />
          </mesh>
          <mesh position={[0.08, -0.25, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
            <boxGeometry args={[0.06, 0.2, 0.07]} />
            <BrassMaterial />
          </mesh>
        </group>
      ))}
      {[-0.12, 0.12].map((z) => (
        <mesh key={z} position={[0.24, 0.3, z]} rotation={[0.25, 0, -0.7]} castShadow>
          <coneGeometry args={[0.035, 0.22, 5]} />
          <BoneMaterial />
        </mesh>
      ))}
    </group>
  )
}

function ShellbackModel({ owner, attacking }: UnitVisualProps) {
  const motion = useRef<Group>(null)
  const tail = useRef<Group>(null)
  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    if (motion.current) {
      motion.current.position.y = 0.012 + Math.abs(Math.sin(time * 5.2)) * 0.012
      motion.current.rotation.z = Math.sin(time * 2.6) * 0.018
    }
    if (tail.current) tail.current.rotation.y = Math.sin(time * (attacking ? 10 : 3.4)) * (attacking ? 0.6 : 0.16)
  })

  return (
    <group ref={motion}>
      <mesh position={[-0.05, 0.38, 0]} scale={[1.35, 0.83, 1.05]} castShadow>
        <dodecahedronGeometry args={[0.34, 0]} />
        <BasaltMaterial />
      </mesh>
      <mesh position={[-0.08, 0.5, 0]} scale={[1.3, 0.62, 1.05]} castShadow>
        <dodecahedronGeometry args={[0.33, 0]} />
        <BoneMaterial />
      </mesh>
      <mesh position={[-0.07, 0.57, 0]} scale={[1.15, 0.22, 0.8]}>
        <dodecahedronGeometry args={[0.28, 0]} />
        <TeamMaterial owner={owner} intensity={1.05} />
      </mesh>
      {[-0.22, 0, 0.22].map((x, index) => (
        <mesh key={x} position={[x - 0.05, 0.76 - Math.abs(index - 1) * 0.035, 0]} castShadow>
          <coneGeometry args={[0.055, 0.2, 5]} />
          <BrassMaterial />
        </mesh>
      ))}
      <mesh position={[0.38, 0.34, 0]} scale={[1.05, 0.72, 0.82]} castShadow>
        <dodecahedronGeometry args={[0.2, 0]} />
        <BoneMaterial />
      </mesh>
      <mesh position={[0.48, 0.36, 0]} scale={[1.1, 0.45, 0.58]}>
        <dodecahedronGeometry args={[0.14, 0]} />
        <TeamMaterial owner={owner} />
      </mesh>
      <group ref={tail} position={[-0.35, 0.37, 0]}>
        <mesh position={[-0.18, 0, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.06, 0.1, 0.4, 7]} />
          <BrassMaterial />
        </mesh>
        <mesh position={[-0.42, 0, 0]} castShadow>
          <dodecahedronGeometry args={[0.15, 0]} />
          <BasaltMaterial />
        </mesh>
        {[-0.08, 0.08].map((z) => (
          <mesh key={z} position={[-0.43, 0.08, z]} rotation={[z > 0 ? -0.4 : 0.4, 0, 0]}>
            <coneGeometry args={[0.035, 0.16, 5]} />
            <BoneMaterial />
          </mesh>
        ))}
      </group>
      {[-0.22, 0.22].flatMap((x) => [-0.22, 0.22].map((z) => (
        <mesh key={`${x}:${z}`} position={[x, 0.16, z]} rotation={[0, 0, x > 0 ? -0.08 : 0.08]} castShadow>
          <boxGeometry args={[0.14, 0.29, 0.12]} />
          <BrassMaterial />
        </mesh>
      )))}
    </group>
  )
}

function RamhornModel({ owner, attacking }: UnitVisualProps) {
  const motion = useRef<Group>(null)
  const head = useRef<Group>(null)
  useFrame(({ clock }) => {
    const time = clock.elapsedTime
    const gait = Math.sin(time * 4.4)
    if (motion.current) {
      motion.current.position.y = 0.01 + Math.abs(gait) * 0.014
      motion.current.rotation.z = gait * 0.018
    }
    if (head.current) {
      const charge = attacking ? Math.pow(Math.max(0, Math.sin(time * 7.5)), 5) : 0
      head.current.position.x = 0.36 + charge * 0.1
      head.current.rotation.z = -0.06 - charge * 0.12
    }
  })

  return (
    <group ref={motion}>
      <mesh position={[-0.08, 0.42, 0]} scale={[1.45, 0.88, 0.9]} castShadow>
        <dodecahedronGeometry args={[0.34, 0]} />
        <BasaltMaterial />
      </mesh>
      <mesh position={[-0.12, 0.55, 0]} scale={[1.35, 0.38, 0.86]}>
        <dodecahedronGeometry args={[0.31, 0]} />
        <TeamMaterial owner={owner} intensity={1.2} />
      </mesh>
      <mesh position={[-0.42, 0.43, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <coneGeometry args={[0.075, 0.35, 6]} />
        <BrassMaterial />
      </mesh>
      <group ref={head} position={[0.36, 0.43, 0]}>
        <mesh position={[-0.08, 0.08, 0]} scale={[0.35, 1, 1]} castShadow>
          <dodecahedronGeometry args={[0.31, 0]} />
          <BoneMaterial />
        </mesh>
        <mesh position={[0.04, -0.03, 0]} scale={[0.95, 0.78, 0.8]} castShadow>
          <dodecahedronGeometry args={[0.22, 0]} />
          <BasaltMaterial />
        </mesh>
        <mesh position={[0.1, 0.04, 0]} scale={[0.78, 0.3, 0.65]}>
          <dodecahedronGeometry args={[0.18, 0]} />
          <TeamMaterial owner={owner} />
        </mesh>
        {[-0.14, 0.14].map((z) => (
          <mesh key={z} position={[0.26, 0.09, z]} rotation={[0, 0, -Math.PI / 2]} castShadow>
            <coneGeometry args={[0.045, 0.35, 6]} />
            <meshStandardMaterial color={PALETTE.alloy} roughness={0.32} metalness={0.62} />
          </mesh>
        ))}
        <mesh position={[0.23, -0.07, 0]} rotation={[0, 0, -Math.PI / 2]} castShadow>
          <coneGeometry args={[0.04, 0.24, 6]} />
          <BrassMaterial />
        </mesh>
      </group>
      {[-0.27, 0.2].flatMap((x) => [-0.21, 0.21].map((z) => (
        <group key={`${x}:${z}`} position={[x, 0.24, z]}>
          <mesh position={[0, -0.1, 0]} castShadow>
            <boxGeometry args={[0.15, 0.3, 0.13]} />
            <BoneMaterial />
          </mesh>
          <mesh position={[0.045, -0.26, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
            <boxGeometry args={[0.09, 0.18, 0.14]} />
            <BrassMaterial />
          </mesh>
        </group>
      )))}
    </group>
  )
}

export function UnitModel({
  type,
  owner,
  attacking = false,
}: {
  type: UnitType
  owner: Owner
  attacking?: boolean
}) {
  const visual = type === 'clawpack'
    ? <ClawpackModel owner={owner} attacking={attacking} />
    : type === 'shellback'
      ? <ShellbackModel owner={owner} attacking={attacking} />
      : <RamhornModel owner={owner} attacking={attacking} />

  return (
    <group rotation={[0, owner === 'player' ? 0 : Math.PI, 0]}>
      {visual}
    </group>
  )
}

function FossilNode() {
  const rocks = [
    { position: [-0.18, 0.14, 0.08] as [number, number, number], scale: [0.23, 0.19, 0.2] as [number, number, number] },
    { position: [0.13, 0.16, -0.12] as [number, number, number], scale: [0.27, 0.24, 0.2] as [number, number, number] },
    { position: [0.22, 0.1, 0.16] as [number, number, number], scale: [0.15, 0.13, 0.17] as [number, number, number] },
  ]
  return (
    <group>
      <mesh position={[0, 0.035, 0]} receiveShadow>
        <cylinderGeometry args={[0.43, 0.48, 0.07, 9]} />
        <BasaltMaterial />
      </mesh>
      {rocks.map((rock, index) => (
        <mesh key={index} position={rock.position} scale={rock.scale} rotation={[0.2 * index, 0.7 * index, -0.12 * index]} castShadow>
          <dodecahedronGeometry args={[1, 0]} />
          <meshStandardMaterial color={index === 1 ? PALETTE.fossil : PALETTE.basaltLight} roughness={0.78} metalness={0.18} />
        </mesh>
      ))}
      {[-0.2, 0.04, 0.24].map((x, index) => (
        <mesh key={x} position={[x, 0.3 + index * 0.04, 0.02 - index * 0.08]} rotation={[0.08, 0, index * 0.18]} castShadow>
          <coneGeometry args={[0.07, 0.35 + index * 0.06, 5]} />
          <meshStandardMaterial color={PALETTE.alloy} roughness={0.42} metalness={0.5} />
        </mesh>
      ))}
    </group>
  )
}

function AmberNode() {
  return (
    <group>
      <mesh position={[0, 0.035, 0]} receiveShadow>
        <cylinderGeometry args={[0.43, 0.48, 0.07, 9]} />
        <BasaltMaterial />
      </mesh>
      {[
        { p: [-0.18, 0.2, 0.06], s: 0.2 },
        { p: [0.08, 0.27, -0.08], s: 0.27 },
        { p: [0.24, 0.15, 0.14], s: 0.15 },
      ].map(({ p, s }, index) => (
        <mesh key={index} position={p as [number, number, number]} scale={[s * 0.8, s * 1.25, s * 0.8]} rotation={[0.1 * index, 0.6 * index, 0.18 * index]} castShadow>
          <icosahedronGeometry args={[1, 0]} />
          <meshStandardMaterial
            color={PALETTE.amber}
            emissive={PALETTE.amberDark}
            emissiveIntensity={0.9}
            transparent
            opacity={0.78}
            roughness={0.2}
            metalness={0.08}
          />
        </mesh>
      ))}
      <mesh position={[0.02, 0.08, 0]}>
        <torusGeometry args={[0.32, 0.035, 6, 20]} />
        <BrassMaterial />
      </mesh>
    </group>
  )
}

export function ResourceNodeModel({ kind }: { kind: 'fossil' | 'amber' }) {
  return kind === 'fossil' ? <FossilNode /> : <AmberNode />
}

export function CargoModel({ kind, owner }: { kind: ResourceKind; owner: Owner }) {
  const accent = teamColor(owner)
  if (kind === 'fossil') {
    return (
      <group>
        <mesh rotation={[0.2, 0.35, 0.1]} castShadow>
          <dodecahedronGeometry args={[0.14, 0]} />
          <meshStandardMaterial color={PALETTE.fossil} roughness={0.68} metalness={0.22} />
        </mesh>
        <mesh position={[0, 0.11, 0]} scale={[1, 0.3, 1]}>
          <octahedronGeometry args={[0.075, 0]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={1.1} />
        </mesh>
      </group>
    )
  }
  if (kind === 'amber') {
    return (
      <group>
        <mesh castShadow>
          <icosahedronGeometry args={[0.145, 1]} />
          <meshStandardMaterial color={PALETTE.amber} emissive={PALETTE.amberDark} emissiveIntensity={0.9} transparent opacity={0.78} roughness={0.16} />
        </mesh>
        <mesh position={[0, -0.12, 0]}>
          <torusGeometry args={[0.09, 0.018, 5, 12]} />
          <meshBasicMaterial color={accent} toneMapped={false} />
        </mesh>
      </group>
    )
  }
  if (kind === 'alloy') {
    return (
      <group>
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
          <cylinderGeometry args={[0.145, 0.145, 0.09, 6]} />
          <meshStandardMaterial color={PALETTE.alloy} roughness={0.28} metalness={0.78} />
        </mesh>
        <mesh position={[0, 0.05, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.07, 0.07, 0.012, 6]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={1.2} />
        </mesh>
      </group>
    )
  }
  return (
    <group>
      <mesh castShadow>
        <cylinderGeometry args={[0.075, 0.075, 0.21, 8]} />
        <meshStandardMaterial color={PALETTE.genome} emissive={'#1f684c'} emissiveIntensity={0.9} transparent opacity={0.82} roughness={0.22} />
      </mesh>
      {[-0.105, 0.105].map((y) => (
        <mesh key={y} position={[0, y, 0]} castShadow>
          <sphereGeometry args={[0.075, 8, 6]} />
          <meshStandardMaterial color={y > 0 ? accent : PALETTE.genome} emissive={y > 0 ? accent : '#1f684c'} emissiveIntensity={1} roughness={0.28} />
        </mesh>
      ))}
    </group>
  )
}
